import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { CheckCircle2, Truck, FileText, Lock, Loader2, AlertTriangle } from "lucide-react";
import api from "../lib/api";
import JunkFolderHint from "../components/JunkFolderHint";
import { metaTrack } from "../lib/meta";

export default function OrderConfirmation() {
  const { orderNumber } = useParams();
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(false);
  const conversionFiredRef = useRef(false);
  const metaPurchaseFiredRef = useRef(false);

  useEffect(() => {
    api.get(`/orders/by-number/${orderNumber}`).then((r) => setOrder(r.data)).catch(() => setError(true));
  }, [orderNumber]);

  const isCard = order?.payment_method === "GlobalPay Card";
  const isPaid = !!order && (order.payment_status === "paid" || ["paid", "processing", "dispatched", "shipped", "delivered"].includes(order.status));
  const isFailed = order?.payment_status === "failed";
  const isSettling = !!order && isCard && !isPaid && !isFailed;

  // Card orders can land here while the gateway is still settling (3DS done,
  // funds authorised, settlement in flight). Keep polling the GlobalPay-aware
  // status endpoint until the order flips paid/failed, then refetch.
  useEffect(() => {
    if (!isSettling || !order?.id) return;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/globalpay/status/${order.id}`);
        if (data.payment_status === "paid" || data.payment_status === "failed") {
          const r = await api.get(`/orders/by-number/${orderNumber}`);
          setOrder(r.data);
        }
      } catch (_) { /* keep polling */ }
    }, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSettling, order?.id]);

  // Fire Google Ads purchase conversion once per order (transaction_id dedupes in Google)
  useEffect(() => {
    if (!order || conversionFiredRef.current) return;
    if (typeof window.gtag !== "function") return;
    if (order.payment_status !== "paid" && order.status !== "paid" && order.status !== "processing") return;
    window.gtag("event", "conversion", {
      send_to: "AW-18137918745/wrpjCN3vi6ccEJna6shD",
      value: Number(order.total || 0),
      currency: "GBP",
      transaction_id: String(order.order_number || ""),
    });
    conversionFiredRef.current = true;
  }, [order]);

  // Fire Meta Pixel Purchase once per order using the stored meta_event_id
  // (same id is sent server-side via CAPI from _mark_order_paid so Meta
  // deduplicates the two events into a single conversion).
  useEffect(() => {
    if (!order || metaPurchaseFiredRef.current) return;
    if (order.payment_status !== "paid" && order.status !== "paid" && order.status !== "processing") return;
    metaTrack.purchase(order, order.meta_event_id);
    metaPurchaseFiredRef.current = true;
  }, [order]);

  if (error) return <div className="max-w-3xl mx-auto py-20 text-center">Order not found</div>;
  if (!order) return <div className="max-w-3xl mx-auto py-20 text-center text-[#9CA8B8]">Loading order...</div>;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16" data-testid="order-confirmation-page">
      {isSettling ? (
        <div className="text-center mb-10" data-testid="order-settling-banner">
          <Loader2 size={56} className="text-[#00C2D6] mx-auto mb-4 animate-spin" />
          <div className="pg-micro mb-2">// Confirming Payment</div>
          <h1 className="text-[#F7FAFC] text-4xl pg-display" style={{ fontFamily: "'Exo 2'" }}>Confirming Your Card Payment</h1>
          <div className="text-[#9CA8B8] mt-2 max-w-xl mx-auto">
            3-D Secure passed and your payment is with the bank. This page updates automatically —
            most payments confirm within a couple of minutes. You&apos;ll receive a confirmation
            email the moment it clears, so it&apos;s safe to close this page.
          </div>
        </div>
      ) : isFailed ? (
        <div className="text-center mb-10" data-testid="order-failed-banner">
          <AlertTriangle size={56} className="text-[#FF8A5B] mx-auto mb-4" />
          <div className="pg-micro mb-2">// Payment Not Completed</div>
          <h1 className="text-[#F7FAFC] text-4xl pg-display" style={{ fontFamily: "'Exo 2'" }}>Payment Not Completed</h1>
          <div className="text-[#9CA8B8] mt-2 max-w-xl mx-auto">
            Your bank didn&apos;t approve this payment, so <strong className="text-[#F7FAFC]">no money has been taken</strong>. Your order is saved
            and the stock is held for you &mdash; try again below.
          </div>
          <div
            className="mt-5 mx-auto max-w-xl text-left rounded-sm border border-[#5EEAD4]/40 bg-[#0B1220] px-4 py-3 text-sm text-[#D5DCE8] leading-relaxed"
            data-testid="order-failed-natwest-tip"
          >
            <strong className="text-[#F7FAFC]">Bank with NatWest, RBS or Ulster Bank?</strong> Their app sometimes needs a second go &mdash; approve the
            payee check when it appears and retry, or pay from an account with another bank. Most second attempts go straight through.
          </div>
          <Link
            to={`/checkout/secure-payment?order=${order.order_number}`}
            className="pg-btn-checkout inline-flex mt-6"
            style={{ width: "auto" }}
            data-testid="order-failed-retry"
          >
            Try payment again
          </Link>
          <div className="mt-3">
            <Link to="/cart" className="text-xs text-[#9CA8B8] underline underline-offset-4 hover:text-[#F7FAFC]" data-testid="order-failed-back-to-cart">
              or go back to your basket
            </Link>
          </div>
        </div>
      ) : (
      <div className="text-center mb-10">
        <CheckCircle2 size={56} className="text-[#A6FF00] mx-auto mb-4" />
        <div className="pg-micro mb-2">// Payment Confirmed</div>
        <h1 className="text-[#F7FAFC] text-4xl pg-display" style={{ fontFamily: "'Exo 2'" }}>Order Received</h1>
        <div className="text-[#9CA8B8] mt-2">
          Thank you. {order.payment_method === "GlobalPay Card" ? "Your card payment has been confirmed." : "Your secure UK bank transfer has been confirmed."}
        </div>
      </div>
      )}

      <div className="mb-5">
        <JunkFolderHint
          email={order.shipping?.email}
          what="your order confirmation"
          size="sm"
        />
      </div>

      <div className="pg-card p-8 mb-6">
        <div className="flex flex-wrap justify-between gap-4 mb-6">
          <div>
            <div className="pg-micro mb-1">Order Number</div>
            <div className="text-[#F7FAFC] pg-mono text-lg" data-testid="order-number">{order.order_number}</div>
          </div>
          <div>
            <div className="pg-micro mb-1">Payment</div>
            {order.payment_method === "GlobalPay Card" ? (
              <>
                <div className="text-[#F7FAFC] text-sm flex items-center gap-1.5" data-testid="order-payment-method"><Lock size={12} className="text-[#5EEAD4]" /> Card{order.globalpay_card_brand ? ` · ${order.globalpay_card_brand}` : ""}{order.globalpay_card_last4 ? ` •••• ${order.globalpay_card_last4}` : ""}</div>
                {order.globalpay_transaction_ref && (
                  <div className="pg-mono text-[10px] text-[#00C2D6] mt-1">Ref: {order.globalpay_transaction_ref}</div>
                )}
                <div className="text-[10px] text-[#9CA8B8] mt-1">Shows on your statement as <span className="text-[#F7FAFC] pg-mono">SEND GLOBAL</span></div>
              </>
            ) : (
              <>
                <div className="text-[#F7FAFC] text-sm flex items-center gap-1.5" data-testid="order-payment-method"><Lock size={12} className="text-[#5EEAD4]" /> Secure UK Bank Transfer</div>
                {order.fena_reference && (
                  <div className="pg-mono text-[10px] text-[#00C2D6] mt-1">Ref: {order.fena_reference}</div>
                )}
              </>
            )}
          </div>
          <div>
            <div className="pg-micro mb-1">Status</div>
            <span className="pg-badge-stock">{isSettling ? "CONFIRMING" : isFailed ? "FAILED" : order.status.toUpperCase()}</span>
          </div>
        </div>

        {order.same_day_eligible && (
          <div className="p-4 border border-[#A6FF00]/40 bg-[#A6FF00]/[0.05] rounded-sm mb-5 flex items-center gap-3">
            <Truck size={18} className="text-[#A6FF00]" />
            <span className="text-sm text-[#F7FAFC]">
              Eligible for <strong>same-day UK dispatch</strong> — expect tracked next-day delivery.
            </span>
          </div>
        )}

        <div className="pg-divider mb-5" />
        <div className="space-y-3 mb-5">
          {order.items.map((i) => (
            <div key={i.sku} className="flex justify-between text-sm">
              <span className="text-[#F7FAFC]">{i.name} {i.strength} × {i.quantity}</span>
              <span className="text-[#F7FAFC] pg-mono">£{(i.price * i.quantity).toFixed(2)}</span>
            </div>
          ))}
        </div>
        <div className="pg-divider mb-4" />
        <div className="flex justify-between mb-2 text-sm"><span className="text-[#9CA8B8]">Subtotal</span><span className="text-[#F7FAFC]">£{order.subtotal.toFixed(2)}</span></div>
        {order.discount > 0 && <div className="flex justify-between mb-2 text-sm"><span className="text-[#9CA8B8]">Discount</span><span className="text-[#A6FF00]">−£{order.discount.toFixed(2)}</span></div>}
        <div className="flex justify-between mb-2 text-sm"><span className="text-[#9CA8B8]">Shipping</span><span className="text-[#F7FAFC]">£{order.shipping_cost.toFixed(2)}</span></div>
        <div className="flex justify-between mt-3 pt-3 border-t border-[#00C2D6]/20">
          <span className="text-[#F7FAFC] pg-heading">{isPaid ? "Total Paid" : "Total"}</span>
          <span className="text-[#A6FF00] pg-display text-2xl" style={{ fontFamily: "'Exo 2'" }}>£{order.total.toFixed(2)}</span>
        </div>
      </div>

      <div className="pg-card p-6 mb-6">
        <div className="pg-micro mb-3">// Dispatch Address</div>
        <div className="text-sm text-[#F7FAFC] leading-relaxed">
          <div>{order.shipping.full_name}</div>
          {order.shipping.organisation && <div>{order.shipping.organisation}</div>}
          <div>{order.shipping.address_line1}</div>
          {order.shipping.address_line2 && <div>{order.shipping.address_line2}</div>}
          <div>{order.shipping.city}, {order.shipping.postcode}</div>
          <div>{order.shipping.country}</div>
        </div>
      </div>

      <div className="p-5 border border-[#A6FF00]/30 bg-[#A6FF00]/[0.04] rounded-sm mb-8">
        <div className="pg-micro text-[#A6FF00] mb-2 flex items-center gap-2">
          <FileText size={12} /> Compliance Reminder
        </div>
        <p className="text-xs text-[#9CA8B8] leading-relaxed">
          All products are supplied strictly for laboratory research use only. Not for human consumption.
        </p>
      </div>

      <div className="text-center">
        <Link to="/products" className="pg-btn-ghost">Back to Catalogue</Link>
      </div>
    </div>
  );
}
